import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { SelectDropdown } from '../SelectDropdown';
import { CheckboxSelect } from '../CheckboxSelect';
import { arrayHasItems } from '../../helpers/common';

export const BudgetModal = ({
    isOpen,
    onClose,
    labelItem,
    periodItem,
    showTemplateEditor = false,
    templateOptions,
    netIncomeOptions,
    netIncomeOptionsLoading,
    selectedNetIncomeItem,
    selectedForecastItem,
    forecastOptions,
    forecastOptionsLoading,
    activeCategories,
    reportCategories,
    onAccept
}) => {
    const [label, setLabel] = useState(labelItem || '');
    const [selectedPeriod, setSelectedPeriod] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedNetIncome, setSelectedNetIncome] = useState(selectedNetIncomeItem);
    const [selectedForecast, setSelectedForecast] = useState(selectedForecastItem);
    const [selectedActiveCategories, setSelectedActiveCategories] = useState(Array.isArray(activeCategories) ? activeCategories : []);

    useEffect(() => {
        if (!isOpen) return;
        if (selectedForecastItem) {
            setSelectedForecast(selectedForecastItem);
        }
        if (selectedNetIncome) {
            setSelectedNetIncome(selectedNetIncome);
        }
        if (labelItem) {
            setLabel(labelItem);
        }
        if (periodItem) {
            setSelectedPeriod(periodItem);
        }
        if (arrayHasItems(activeCategories)) {
            setSelectedActiveCategories(activeCategories);
        } else {
            setSelectedActiveCategories((reportCategories || []).map((category) => category.id));
        }
    }, [isOpen, activeCategories, selectedForecastItem, labelItem, periodItem, selectedNetIncome, reportCategories]);

    const handleAcceptActions = async () => {
        if (!label || !selectedPeriod || !selectedNetIncome) {
            toast.error(`Empty budget options. Name, period and net income are required.`, { position: "bottom-right" });
            return;
        }
        await onAccept({
            name: label,
            period: selectedPeriod,
            selectedForecast,
            activeCategories: selectedActiveCategories,
            selectedTemplate,
            selectedNetIncome
        });
    }

    const handleClose = () => {
        onClose();
        setLabel('');
    };

    // Handle Esc key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={handleClose}
                ></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="inline-block transform rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg font-bold mb-2">Budget</h3>
                        <div className="flex flex-col gap-1 mb-1">
                            <span>Name:</span>
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={label}
                                    onChange={(e) => {
                                        setLabel(e.target.value)
                                    }}
                                    className="w-full p-2.5 shadow-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                            </div>
                         </div>
                        <div className="flex flex-col gap-1 mb-1">
                            <span>Period:</span>
                            <div className="flex-1">
                                <SelectDropdown
                                    options={[
                                        { label: "2025", value: "2025" }, 
                                        { label: "2026", value: "2026" },
                                        { label: "2027", value: "2027" },
                                        { label: "2028", value: "2028" },
                                        { label: "2029", value: "2029" },
                                        { label: "2030", value: "2030" },
                                    ]}
                                    value={selectedPeriod}
                                    onChange={setSelectedPeriod}
                                    placeholder=""
                                    selectClassname="bg-white"
                                />
                            </div>
                         </div>
                         <div className="flex flex-col gap-1 mb-1">
                            <span>Net Income Calculation:</span>
                            <div className="flex-1">
                                <SelectDropdown
                                    key={`${selectedNetIncome}_${(netIncomeOptions || []).length}`}
                                    options={netIncomeOptions || []}
                                    value={selectedNetIncome}
                                    disabled={netIncomeOptionsLoading}
                                    onChange={setSelectedNetIncome}
                                    placeholder=""
                                    itemValueClassname="px-2 py-1 text-sm text-left"
                                    selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                    selectClassname="!bg-white !px-3 !py-2 !border-input"
                                />
                            </div>
                         </div>
                        <div className="flex flex-col gap-1 mb-1">
                            <span>Source Demand Plan:</span>
                            <div className="flex-1">
                                <SelectDropdown
                                    key={`${selectedForecast}_${(forecastOptions || []).length}`}
                                    options={forecastOptions || []}
                                    value={selectedForecast}
                                    disabled={forecastOptionsLoading}
                                    onChange={setSelectedForecast}
                                    placeholder=""
                                    itemValueClassname="px-2 py-1 text-sm text-left"
                                    selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                    selectClassname="!bg-white !px-3 !py-2 !border-input"
                                />
                            </div>
                         </div>
                         <p className="mb-3 text-xs text-gray-500">Changing the source does not rewrite values. Save, then use Refresh from Demand Plan to rebase open and future months.</p>
                         <details className="mb-3 rounded border border-gray-200 p-2">
                            <summary className="cursor-pointer text-sm font-medium text-gray-700">Advanced</summary>
                            <div className="mt-2 flex flex-col gap-1">
                                <span>Active Categories:</span>
                                <CheckboxSelect
                                    options={(reportCategories || []).map((category) => ({
                                        label: category.name,
                                        value: category.id
                                    }))}
                                    selectedOptions={selectedActiveCategories || []}
                                    onChange={setSelectedActiveCategories}
                                    containerClassName="!w-full"
                                    dropdownClassName="!w-[90%]"
                                    inputPlaceholder="Type category name to search"
                                    placeholder="Select active categories"
                                />
                            </div>
                         </details>
                         {showTemplateEditor && (
                            <div className="flex flex-col gap-1 mb-1">
                                <span>Template:</span>
                                <div className="flex-1">
                                    <SelectDropdown
                                        key={`${selectedForecast}_${(forecastOptions || []).length}`}
                                        options={templateOptions || []}
                                        value={selectedTemplate}
                                        disabled={false}
                                        onChange={setSelectedTemplate}
                                        placeholder=""
                                        itemValueClassname="px-2 py-1 text-sm text-left"
                                        selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
                                        selectClassname="!bg-white !px-3 !py-2 !border-input"
                                    />
                                </div>
                            </div>
                         )}
                         <div className="py-3 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleAcceptActions}
                                >
                                    Accept
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                         </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};